import type { AutoReplyInteraction, MailMessage } from '../types/api';

export type TrailKind = 'outbound' | 'inbound' | 'auto-reply';

export interface TrailMessage {
  /** Stable key: `mail-<id>` for vendor_messages rows, `auto-<messageId>` for auto-replies. */
  id: string;
  kind: TrailKind;
  /** Chronological sort key (ISO-ish). */
  at: string | null;
  /** Set when kind is 'outbound' or 'inbound'. */
  mail: MailMessage | null;
  /** Set when kind is 'auto-reply'. */
  autoReply: AutoReplyInteraction | null;
}

export interface Conversation {
  /** threadToken when present, else `vendor-${vendorId}`, else 'unassigned'. */
  id: string;
  vendorId: number | null;
  vendorCode: string | null;
  vendorName: string | null;
  threadToken: string | null;
  subject: string | null;
  /** Chronological (oldest first) merged trail. */
  messages: TrailMessage[];
  latestAt: string | null;
  lastKind: TrailKind | null;
  messageCount: number;
  hasAttachment: boolean;
}

/** Which conversation bucket a vendor_messages row belongs to. */
function conversationKey(message: MailMessage): string {
  if (message.threadToken != null) return `token:${message.threadToken}`;
  if (message.vendorId != null) return `vendor:${message.vendorId}`;
  return 'unassigned';
}

/** The row that best represents a conversation (prefer the outbound RFP mail). */
function representativeMessage(messages: MailMessage[]): MailMessage | undefined {
  return (
    messages.find((message) => message.direction === 'outbound' && message.kind === 'rfp') ??
    messages[0]
  );
}

/** Null-safe chronological comparison: nulls sort last. */
function compareTimestamps(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b);
}

function trailMessageNumber(message: TrailMessage): number | null {
  if (message.mail) return message.mail.id;
  if (message.autoReply) return message.autoReply.messageId;
  return null;
}

function sortTrail(messages: TrailMessage[]): void {
  messages.sort((a, b) => {
    const byTime = compareTimestamps(a.at, b.at);
    if (byTime !== 0) return byTime;

    const aId = trailMessageNumber(a);
    const bId = trailMessageNumber(b);
    if (aId === null && bId === null) return a.id.localeCompare(b.id);
    if (aId === null) return 1;
    if (bId === null) return -1;
    if (aId !== bId) return aId - bId;
    return a.id.localeCompare(b.id);
  });
}

function isRejectedOrFailed(interaction: AutoReplyInteraction): boolean {
  return (
    interaction.messageStatus === 'rejected' ||
    interaction.messageStatus === 'failed' ||
    interaction.reply?.status === 'failed' ||
    interaction.reply?.errorCode != null
  );
}

export function groupConversations(
  thread: MailMessage[],
  autoReplies: AutoReplyInteraction[],
): Conversation[] {
  const mailRows = Array.isArray(thread) ? thread : [];
  const interactions = Array.isArray(autoReplies) ? autoReplies : [];

  // 1. Dedupe vendor_messages rows defensively: keep the first row per id.
  const seenMailIds = new Set<number>();
  const dedupedRows: MailMessage[] = [];
  for (const message of mailRows) {
    if (message.id != null && seenMailIds.has(message.id)) continue;
    if (message.id != null) seenMailIds.add(message.id);
    dedupedRows.push(message);
  }

  // 2. Group rows by threadToken, else vendorId, else into a single bucket.
  const buckets = new Map<string, MailMessage[]>();
  for (const message of dedupedRows) {
    const key = conversationKey(message);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(message);
    else buckets.set(key, [message]);
  }

  // 3. Build one conversation per bucket.
  const conversations: Conversation[] = [];
  for (const bucket of buckets.values()) {
    const representative = representativeMessage(bucket);
    if (!representative) continue;

    const conversation: Conversation = {
      id:
        representative.threadToken ??
        (representative.vendorId != null ? `vendor-${representative.vendorId}` : 'unassigned'),
      vendorId: representative.vendorId,
      vendorCode: representative.vendorCode,
      vendorName: representative.vendorName,
      threadToken: representative.threadToken,
      subject: representative.subject,
      messages: [],
      latestAt: null,
      lastKind: null,
      messageCount: 0,
      hasAttachment: false,
    };

    const seenTrailIds = new Set<string>();
    for (const message of bucket) {
      const trail: TrailMessage = {
        id: `mail-${message.id}`,
        kind: message.direction === 'outbound' ? 'outbound' : 'inbound',
        at: message.at,
        mail: message,
        autoReply: null,
      };
      if (seenTrailIds.has(trail.id)) continue;
      seenTrailIds.add(trail.id);
      conversation.messages.push(trail);
    }

    conversations.push(conversation);
  }

  // 4. Attach auto-replies (the outbound automated-reply half only).
  for (const interaction of interactions) {
    if (!interaction.reply) continue;

    const targetId = `auto-${interaction.messageId}`;

    let conversation: Conversation | undefined;
    if (interaction.vendorId != null) {
      conversation = conversations.find((item) => item.vendorId === interaction.vendorId);
    } else {
      // Unmatched interactions only land in the unassigned bucket when they
      // were not rejected/failed; those stay visible via rejectedOrFailed.
      if (isRejectedOrFailed(interaction)) continue;
      conversation = conversations.find((item) => item.id === 'unassigned');
    }

    if (!conversation) continue;
    if (conversation.messages.some((item) => item.id === targetId)) continue;

    conversation.messages.push({
      id: targetId,
      kind: 'auto-reply',
      at: interaction.reply.sentAt ?? interaction.reply.createdAt ?? interaction.receivedAt,
      mail: null,
      autoReply: interaction,
    });
  }

  // 5. Finalise aggregate fields and sort each trail oldest-first.
  for (const conversation of conversations) {
    sortTrail(conversation.messages);

    let latestAt: string | null = null;
    let lastKind: TrailKind | null = null;
    for (let index = conversation.messages.length - 1; index >= 0; index -= 1) {
      const message = conversation.messages[index]!;
      if (message.at == null) continue;
      latestAt = message.at;
      lastKind = message.kind;
      break;
    }
    if (latestAt === null && conversation.messages.length > 0) {
      lastKind = conversation.messages[conversation.messages.length - 1]!.kind;
    }

    conversation.latestAt = latestAt;
    conversation.lastKind = lastKind;
    conversation.messageCount = conversation.messages.length;
    conversation.hasAttachment = conversation.messages.some(
      (message) =>
        (message.mail?.attachmentCount ?? 0) > 0 ||
        (message.autoReply?.attachmentCount ?? 0) > 0,
    );
  }

  // 6. Deterministic ordering: latestAt desc, vendorName asc, then id asc.
  return conversations.sort(compareConversations);
}

function compareConversations(a: Conversation, b: Conversation): number {
  if (a.latestAt !== null || b.latestAt !== null) {
    if (a.latestAt === null) return 1;
    if (b.latestAt === null) return -1;
    const byLatest = b.latestAt.localeCompare(a.latestAt);
    if (byLatest !== 0) return byLatest;
  }

  if (a.vendorName !== null || b.vendorName !== null) {
    if (a.vendorName === null) return 1;
    if (b.vendorName === null) return -1;
    const byName = a.vendorName.localeCompare(b.vendorName);
    if (byName !== 0) return byName;
  }

  return a.id.localeCompare(b.id);
}

export function rejectedOrFailed(
  autoReplies: AutoReplyInteraction[],
): AutoReplyInteraction[] {
  return (Array.isArray(autoReplies) ? autoReplies : []).filter(isRejectedOrFailed);
}

